import React from "react";
import {
  ContentLayout,
  DebouncedSearch,
  HeaderBreadcrumbs,
  ServerPaginatedTable,
  usePermission,
} from "../../../shared";

import { Button, Chip, Stack, Typography } from "@mui/material";
import { EmailLogListFilterSection } from "../components";
import { useEmailLogList } from "../hooks";

export const EmailLogList = () => {
  const {
    breadcrumbs,
    state,
    gotoPage,
    handleSortBy,
    setPageSize,
    handleSearch,
    handleFilterChange,
    handleDateChangeApply,
    handleOpenDateRangeSelection,
    handleApplyFilter,
    handleClearAllFilter,
    handleClearFilter,
    handleResendEmail,
  } = useEmailLogList();
  const hasResendPermission = usePermission("email-log-create");

  const columns = React.useMemo(() => {
    return [
      {
        Header: "Info",
        accessor: "subject",
        disableSortBy: true,
        Cell: ({ value, row }) => {
          return (
            <React.Fragment>
              <Typography variant="body2">{value}</Typography>
              <Stack
                gap="4px"
                marginTop="4px"
                flexDirection="row"
                flexWrap="wrap"
                maxWidth="565px"
              >
                {row.original?.to_recipient?.map((item) => {
                  return (
                    <Typography
                      key={item.email}
                      variant="caption"
                      color="GrayText"
                    >
                      {item.email};
                    </Typography>
                  );
                })}
              </Stack>
            </React.Fragment>
          );
        },
      },
      {
        Header: "Type",
        accessor: "mail_class",
      },
      {
        Header: "Date & Time",
        accessor: "sent_at",
      },
      {
        Header: "Status",
        accessor: "status",
        Cell: ({ value }) => {
          const color = value === "Delivered" ? "#0F875B" : "#C42B1C";
          const backgroundColor = value === "Delivered" ? "#E1FBF2" : "#FFE7E3";

          return value ? (
            <Chip
              label={value}
              sx={{ color: color, backgroundColor: backgroundColor }}
            />
          ) : (
            "--"
          );
        },
      },
      {
        Header: "Action",
        accessor: "action",
        disableSortBy: true,
        align: "right",
        Cell: ({ row }) => {
          return (
            <Stack
              flexDirection="row"
              alignItems="center"
              justifyContent="end"
              gap="8px"
            >
              {hasResendPermission && (
                <Button
                  size="small"
                  onClick={(event) => {
                    event.stopPropagation();
                    event.preventDefault();
                    handleResendEmail(row.original.id);
                  }}
                  disabled={state.resendButtonLoading === row.original.id}
                  sx={{
                    "&.MuiButton-textPrimary": {
                      backgroundColor: "transparent !important",
                      border: "none !important",
                    },
                  }}
                >
                  {state.resendButtonLoading === row.original.id
                    ? "Sending..."
                    : "Resend"}
                </Button>
              )}
              <img alt="details" src="/icons/arrow_forward.svg" />
            </Stack>
          );
        },
      },
    ];
  }, [state.resendButtonLoading]);

  return (
    <ContentLayout
      title="Email Log"
      breadcrumbs={<HeaderBreadcrumbs links={breadcrumbs} />}
    >
      <ServerPaginatedTable
        columns={columns}
        data={state.emailLogs}
        pagination={state.pagination}
        isLoading={state.isLoading}
        gotoPage={gotoPage}
        searchBar={true}
        defaultSearch={false}
        handleSort={handleSortBy}
        setPageSize={setPageSize}
        sortByGlobal={state.sortBy}
        onAllFilterClear={handleClearAllFilter}
        onFilterClear={handleClearFilter}
        filterContent={
          <DebouncedSearch
            placeholder="Search"
            value={state.search}
            onChange={handleSearch}
          />
        }
        filterSection={
          <Stack
            flexDirection="row"
            justifyContent="space-between"
            gap="16px"
            width="100%"
            flexWrap="wrap"
          >
            <EmailLogListFilterSection
              filter={state.filter}
              onFilterChange={handleFilterChange}
              onDateChangeApply={handleDateChangeApply}
              onOpenDateRangeSelection={handleOpenDateRangeSelection}
            />
            <Button
              size="medium"
              variant="contained"
              onClick={() => {
                handleApplyFilter(state);
              }}
              disabled={state.filter.isFilterApplied}
            >
              Apply
            </Button>
          </Stack>
        }
        filterApplied={state.filter.appliedFilters}
        onRowLink={(row) => `${row?.id}/details`}
      />
    </ContentLayout>
  );
};
